import { useUser } from 'contexts/UserContext';
import { Dispatch, SetStateAction, useEffect, useMemo, useState } from 'react';

type ValueVersion = number | string;

type StoredValue<V> = {
    version: ValueVersion;
    value: V;
};

const storageSources = {
    local: localStorage,
    session: sessionStorage,
};

function getFallbackValue<T>(fallbackValue: T | (() => T)): T {
    const value = fallbackValue instanceof Function ? fallbackValue() : fallbackValue;

    if (value === undefined) {
        throw new Error('"undefined" is not a valid value.');
    }

    return value;
}

/**
 * The type of storage to use
 */
export type StoreType = 'local' | 'session';

type SerializeValue<T, V> = (value: T) => V;
type DeserializeValue<T, V> = (value: V) => T;

export type Serialization<T, V> = Readonly<{
    serialize: SerializeValue<T, V>;
    deserialize: DeserializeValue<T, V>;
}>;

export type CoreStorageOptions = Readonly<{
    /**
     * The type of storage to use (defaults to 'local').
     */
    store?: StoreType;

    /**
     * The version of the value (defaults to 1).  If data is loaded from storage with
     * a different version number, the initial value will be used.
     */
    version?: ValueVersion;
}>;

export type StorageOptions<T, V> = CoreStorageOptions & {
    /**
     * Custom serializer and deserializer to handle objects that cannot be serialized normally.
     */
    serialization?: Serialization<T, V>;
};

export type StorageSaveOptions<T, V> = CoreStorageOptions &
    Readonly<{
        /**
         * Custom serializer to handle objects that cannot be serialized normally.
         */
        serialize?: SerializeValue<T, V> | undefined;
    }>;

export type StorageLoadOptions<T, V> = CoreStorageOptions &
    Readonly<{
        /**
         * Custom deserializer to handle objects that cannot be serialized normally.
         */
        deserialize?: DeserializeValue<T, V> | undefined;
    }>;

/**
 * Saves a value to storage.
 *
 * @param key The key used for storage.
 * @param value The value to store.
 * @param options Additional options.
 */
export function saveValueToStorage<T, V = unknown>(
    key: string,
    value: T,
    options?: StorageSaveOptions<T, V>
): void {
    const { store, version } = getStoreAndVersion(options);
    const serializer = options?.serialize ?? ((val: T) => val);

    store.setItem(
        key,
        JSON.stringify({
            version,
            value: serializer(value),
        } as StoredValue<V>)
    );
}

const buildUserStorageKey = (email: string, key: string) => `${email}:${key}`;

export function saveUserValueToStorage<T, V = unknown>(
    email: string,
    key: string,
    value: T,
    options?: StorageSaveOptions<T, V>
): void {
    saveValueToStorage(buildUserStorageKey(email, key), value, options);
}

export function clearUserValueFromStorage(email: string, key: string, store: StoreType) {
    storageSources[store].removeItem(buildUserStorageKey(email, key));
}

/**
 * Loads a value from storage.
 *
 * @param key The key for storage lookup.
 * @param fallbackValue The value to use if value not found, version mismatch, or deserialization error.
 * @param options Additional options.
 */
export function loadValueFromStorage<T, V = unknown>(
    key: string,
    fallbackValue: T | (() => T),
    options?: StorageLoadOptions<T, V>
): T {
    const { store, version } = getStoreAndVersion(options);
    const deserializer = options?.deserialize ?? ((val: V) => (val as unknown) as T);
    const rawStorageValue = store.getItem(key);

    if (rawStorageValue) {
        try {
            const valueFromStorage = JSON.parse(rawStorageValue) as StoredValue<V>;

            if (
                valueFromStorage.version === version &&
                valueFromStorage.value !== undefined
            ) {
                return deserializer(valueFromStorage.value);
            }
        } catch {
            return getFallbackValue(fallbackValue);
        }
    }

    return getFallbackValue(fallbackValue);
}

export function loadUserValueFromStorage<T, V = unknown>(
    email: string,
    key: string,
    fallbackValue: T | (() => T),
    options?: StorageLoadOptions<T, V>
): T {
    return loadValueFromStorage(buildUserStorageKey(email, key), fallbackValue, options);
}

function getStoreAndVersion(options?: CoreStorageOptions) {
    return {
        store: storageSources[options?.store ?? 'local'],
        version: options?.version ?? 1,
    };
}

/**
 * Works like `React.useState` but also stores the value in storage
 * (`localStorage` or `sessionStorage`) for the current user.
 * Defaults to `localStorage`.
 *
 * Note: Use stable values for key, initialValue, and options.
 *
 * @param key The key used for storage (the current user will prefix the key: example: sample.key => test@kcftech.com:sample.key).
 * @param initialValue The initial value or a function to compute the initial value to be used if a valid value does not exist in storage.
 * @param options Configuration options.
 * @returns An array [current value, setter (stable across renders)]
 */
export function useStoredState<T, V = unknown>(
    key: string,
    initialValue: T | (() => T),
    options?: StorageOptions<T, V>
): [T, Dispatch<SetStateAction<T>>] {
    const { load, useWatch } = useStorage(key, initialValue, options);
    const [value, setValue] = useState<T>(load);

    useWatch(value);

    return [value, setValue];
}

export type StorageController<T> = Readonly<{
    /**
     * Attempts to load value from storage.  Initial value is returned on failure.
     */
    load: () => T;

    /**
     * Saves value to local storage.
     */
    save: (updatedValue: T) => void;

    /**
     * Sets up a useEffect that automatically saves the value to local storage on change.
     * NOTE: If called, it must be called on every render.
     * @param value The value to save into local storage.
     * @param enabled (optional) Whether or not persist the value into local storage.
     * If false, then that hook call will not change the stored value.
     */
    useWatch: (value: T, enabled?: boolean) => void;
}>;

/**
 * Creates an interface for loading values from and saving values to local storage
 * for the current user.   Defaults to `localStorage`.
 *
 * Note: Use stable values for key, initialValue, and options.
 *
 * @param key The key used for storage (the current user will prefix the key: example: sample.key => test@kcftech.com:sample.key).
 * @param initialValue The initial value or a function to compute the initial value to be used if a valid value does not exist in storage.
 * @param options Configuration options.
 * @returns An array [current value, setter (stable across renders)]
 */
export function useStorage<T, V = unknown>(
    key: string,
    initialValue: T | (() => T),
    options?: StorageOptions<T, V>
): StorageController<T> {
    const { user } = useUser();

    const { store, version, deserialize, serialize } = useMemo(
        () => ({
            store: options?.store ?? 'local',
            version: options?.version ?? 1,
            deserialize: options?.serialization?.deserialize,
            serialize: options?.serialization?.serialize,
        }),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        []
    );

    return useMemo<StorageController<T>>(
        () => {
            const save = (newValue: T) =>
                saveUserValueToStorage<T, V>(user?.Email ?? '', key, newValue, {
                    store,
                    version,
                    serialize,
                });

            return {
                load: () =>
                    loadUserValueFromStorage<T, V>(user?.Email ?? '', key, initialValue, {
                        store,
                        version,
                        deserialize,
                    }),
                save,
                useWatch: (value: T, enabled = true) => {
                    useEffect(() => {
                        if (enabled) {
                            save(value);
                        }
                    }, [enabled, value]);
                },
            };
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [user?.Email]
    );
}
